import { useCallback } from "react";

import { getDiffDaysByDay } from "@/lib/dayjs/get-diff-days-by-day";
import type {
	ReservationListOptionInfoItem,
	ReservationListRoomInfoItem,
	ReservationListVoucherInfo,
} from "@/lib/gql/__generated__/graphql";
import { PaymentMethod, Reservation_List_Reservation_Type } from "@/lib/gql/__generated__/graphql";

// -------------------------------------------

type CalcTotalVoucherAmountProps = {
	voucherList: Array<ReservationListVoucherInfo>;
};
type CalcTotalVoucherAmountResult = {
	totalVoucherAmount: number;
};

type CalcTotalDiscountAmountProps = {
	discountAmount: number;
	totalVoucherAmount: number;
};
type CalcTotalDiscountAmountResult = {
	totalDiscountAmount: number;
};

type CalcPrevTotalGuestChargeAmountProps = {
	totalGuestChargeAmount: number;
	totalDiscountAmount: number;
};
type CalcPrevTotalGuestChargeAmountResult = {
	prevTotalGuestChargeAmount: number;
};

type CalcTotalStayCountProps = {
	checkIn: string;
	checkOut: string;
};
type CalcTotalStayCountResult = {
	totalStayCount: number;
};

// -------------------------------------------

type GetVoucherNameListProps = {
	voucherList: Array<ReservationListVoucherInfo>;
};
type GetVoucherNameListResult = {
	voucherNameList: Array<string>;
};

type GetCheckOutDateProps = {
	reservationType: Array<Reservation_List_Reservation_Type>;
	checkIn: string;
	checkOut: string;
};
type GetCheckOutDateResult = {
	checkOutDate: string;
};

type GetPaymentMethodTextProps = {
	paymentMethod: PaymentMethod;
};
type GetPaymentMethodTextResult = {
	paymentMethodText: string;
};

type GetNoShowTextProps = {
	noShow: boolean;
};
type GetNoShowTextResult = {
	noShowText: string;
};

type GetPlanNameListProps = {
	planList: Array<string>;
};
type GetPlanNameListResult = {
	planNameList: Array<string>;
};

type GetOptionNameListProps = {
	optionList: Array<ReservationListOptionInfoItem>;
};
type GetOptionNameListResult = {
	optionNameList: Array<string>;
};

type GetRoomNameListProps = {
	roomList: Array<ReservationListRoomInfoItem>;
};
type GetRoomNameListResult = {
	roomNameList: Array<string>;
};

type GetDiscountNameAndDiscountPriceProps = {
	discount: string | undefined;
	discountPrice: number | undefined;
};
type GetDiscountNameAndDiscountPriceResult = {
	discountNameAndDiscountPriceText: string;
};

type GetCheckInTimeTextProps = {
	checkInTimeStart: string;
	checkInTimeEnd: string;
};
type GetCheckInTimeTextResult = {
	checkInTimeText: string;
};

type GetGuestTelTextProps = {
	guestTel: string;
};
type GetGuestTelTextResult = {
	guestTelText: string;
};
// -------------------------------------------
/**
 * 予約に関する処理をまとめる
 */
export const useHandleReservation = () => {
	/**
	 * 宿泊券の合計割引金額を算出する
	 */
	const onCalcTotalVoucherAmount = useCallback(
		({ voucherList }: CalcTotalVoucherAmountProps): CalcTotalVoucherAmountResult => {
			const totalVoucherAmount = voucherList.reduce((acc, voucher) => acc + voucher.price, 0);
			return { totalVoucherAmount };
		},
		[],
	);

	/**
	 * ディスカウントの合計金額を算出する
	 */
	const onCalcTotalDiscountAmount = useCallback(
		({ discountAmount, totalVoucherAmount }: CalcTotalDiscountAmountProps): CalcTotalDiscountAmountResult => {
			/**
			 * 宿泊券とディスカウントの割引を合計する
			 */
			const totalDiscountAmount = totalVoucherAmount + discountAmount;
			return { totalDiscountAmount };
		},
		[],
	);

	/**
	 * ディスカウント前のゲストが支払う金額を算出する
	 */
	const onCalcPrevTotalGuestChargeAmount = useCallback(
		({
			totalGuestChargeAmount,
			totalDiscountAmount,
		}: CalcPrevTotalGuestChargeAmountProps): CalcPrevTotalGuestChargeAmountResult => {
			/**
			 * ディスカウント前のゲストが支払う金額を算出する
			 */
			const prevTotalGuestChargeAmount = totalGuestChargeAmount + totalDiscountAmount;
			return { prevTotalGuestChargeAmount };
		},
		[],
	);

	/**
	 * 通算での宿泊数を算出する
	 */
	const onCalcTotalStayCount = useCallback(
		({ checkIn, checkOut }: CalcTotalStayCountProps): CalcTotalStayCountResult => {
			const totalStayCount = getDiffDaysByDay(checkOut, checkIn);

			return { totalStayCount };
		},
		[],
	);

	/**
	 * 宿泊券名をリストで返却する
	 */
	const onGetVoucherNameList = useCallback(({ voucherList }: GetVoucherNameListProps): GetVoucherNameListResult => {
		const voucherNameList = voucherList.map((voucher) => voucher.name);
		return { voucherNameList };
	}, []);

	/**
	 * チェックアウト日を返却する
	 */
	const onGetCheckOutDate = useCallback(
		({ reservationType, checkIn, checkOut }: GetCheckOutDateProps): GetCheckOutDateResult => {
			const checkOutDate = reservationType.includes(Reservation_List_Reservation_Type.DayTrip)
				? checkIn
				: checkOut;
			return { checkOutDate };
		},
		[],
	);

	/**
	 * 支払方法テキストを返却する
	 */
	const onGetPaymentMethodText = useCallback(
		({ paymentMethod }: GetPaymentMethodTextProps): GetPaymentMethodTextResult => {
			let paymentMethodText = "テスト予約";

			if (paymentMethod === PaymentMethod.Credit) {
				paymentMethodText = "事前決済";
			}

			if (paymentMethod === PaymentMethod.Local) {
				paymentMethodText = "現地決済";
			}

			return { paymentMethodText };
		},
		[],
	);

	/**
	 * NO SHOW テキストを返却する
	 */
	const onGetNoShowText = useCallback(({ noShow }: GetNoShowTextProps): GetNoShowTextResult => {
		const noShowText = noShow ? "NO SHOW" : "";
		return { noShowText };
	}, []);

	/**
	 * プラン名をリストで返却する
	 */
	const onGetPlanNameList = useCallback(({ planList }: GetPlanNameListProps): GetPlanNameListResult => {
		const planNameList = planList.map((plan) => plan);
		return { planNameList };
	}, []);

	/**
	 * オプション名をリストで返却する
	 */
	const onGetOptionNameList = useCallback(({ optionList }: GetOptionNameListProps): GetOptionNameListResult => {
		const optionNameList = optionList.map(({ date, name, amount }) => {
			if (date) {
				return `【日毎の適用オプション】 ${name} ×${amount}個`;
			}

			return `【予約を通じてのオプション】 ${name} × ${amount}個`;
		});
		return { optionNameList };
	}, []);

	/**
	 * ディスカウント名とディスカウント金額をテキストにして返却する
	 */
	const onGetDiscountNameAndDiscountPrice = useCallback(
		({ discount, discountPrice }: GetDiscountNameAndDiscountPriceProps): GetDiscountNameAndDiscountPriceResult => {
			if (!discount || !discountPrice) {
				return { discountNameAndDiscountPriceText: "" };
			}

			return { discountNameAndDiscountPriceText: `${discount} -¥${discountPrice}` };
		},
		[],
	);

	/**
	 * 部屋名をリストで返却する
	 */
	const onGetRoomNameList = useCallback(({ roomList }: GetRoomNameListProps): GetRoomNameListResult => {
		const roomNameList = roomList.map((room) => room.name);
		return { roomNameList };
	}, []);

	/**
	 * チェックイン時刻を返却する
	 */
	const onGetCheckInTimeText = useCallback(
		({ checkInTimeStart, checkInTimeEnd }: GetCheckInTimeTextProps): GetCheckInTimeTextResult => {
			const checkInTimeText = `${checkInTimeStart}~${checkInTimeEnd}`;
			return { checkInTimeText };
		},
		[],
	);

	const onGetGuestTelText = useCallback(({ guestTel }: GetGuestTelTextProps): GetGuestTelTextResult => {
		// 先頭が0の場合に丸られないように `'` で囲む
		const guestTelText = `'${guestTel}'`;
		return { guestTelText };
	}, []);

	return {
		// 宿泊券の合計割引金額を算出する
		onCalcTotalVoucherAmount,
		// ディスカウントの合計金額を算出する
		onCalcTotalDiscountAmount,
		// ディスカウント前のゲストが支払う金額を算出する
		onCalcPrevTotalGuestChargeAmount,
		// 通算での宿泊数を算出する
		onCalcTotalStayCount,
		// 宿泊券名をリストで返却する
		onGetVoucherNameList,
		// チェックアウト日を返却する
		onGetCheckOutDate,
		// 支払方法テキストを返却する
		onGetPaymentMethodText,
		// NO SHOW テキストを返却する
		onGetNoShowText,
		// プラン名をリストで返却する
		onGetPlanNameList,
		// オプション名をリストで返却する
		onGetOptionNameList,
		// 部屋名をリストで返却する
		onGetRoomNameList,
		// ディスカウント名とディスカウント金額をテキストにして返却する
		onGetDiscountNameAndDiscountPrice,
		// チェックイン時刻を返却する
		onGetCheckInTimeText,
		// 電話番号を返却する
		onGetGuestTelText,
	};
};
